import DebounceInput from "common/components/DebounceInput/DebounceInput";
import React from "react";
import type { IconNames } from "components/Icon/Icon";

type CrudFiltersRenderType = "input" | "custom" | "switch";

export type CrudFilter = {
  name: string;
  label?: string;
  renderType: CrudFiltersRenderType;
  renderComponent?: React.ReactNode;
  className?: string;
  labelClassName?: string;
  icon?: IconNames;
  switchConfig?: {
    onFilter: { name: string; value: any };
    offFilter: { name: string; value: any };
  };
};

export interface ICrudFiltersProps {
  filters: CrudFilter[];
  activeFilters?: Record<string, any>;
  setFilter: (name: string, value: any) => void;
  className?: string;
}

function CrudFilters({
  filters,
  activeFilters,
  setFilter,
  className,
}: ICrudFiltersProps) {
  return (
    <div
      className={`crud-filters flex flex-col lg:flex-row gap-4 items-start lg:items-center ${
        className || ""
      }`}
    >
      {filters.map((filter) => {
        const filterValue = activeFilters?.[filter.name];
        const isActive =
          filterValue !== undefined &&
          filterValue !== null &&
          filterValue !== "" &&
          (!Array.isArray(filterValue) || filterValue.length > 0);

        if (filter.renderType === "switch") {
          const { switchConfig } = filter;
          const isOn = switchConfig
            ? !!activeFilters?.[switchConfig.onFilter.name]
            : !!filterValue;

          const handleSwitchClick = () => {
            if (switchConfig) {
              if (isOn) {
                setFilter(switchConfig.onFilter.name, undefined);
                setFilter(
                  switchConfig.offFilter.name,
                  switchConfig.offFilter.value
                );
              } else {
                setFilter(switchConfig.offFilter.name, undefined);
                setFilter(
                  switchConfig.onFilter.name,
                  switchConfig.onFilter.value
                );
              }
            } else {
              setFilter(filter.name, !filterValue);
            }
          };

          return (
            <div key={filter.name} className={filter.className}>
              <label
                className={`flex items-center gap-2 cursor-pointer select-none text-sm ${
                  filter.labelClassName ?? "text-slate-600"
                }`}
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={isOn}
                  aria-label={filter.label || filter.name}
                  onClick={handleSwitchClick}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                    isOn ? "bg-primary" : "bg-slate-200"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                      isOn ? "translate-x-4" : "translate-x-1"
                    }`}
                  />
                </button>
                {filter.label}
              </label>
            </div>
          );
        }

        return (
          <div key={filter.name} className={filter.className}>
            {filter.renderType === "input" ? (
              <DebounceInput
                id={filter.name}
                placeholder={filter.label || ""}
                value={activeFilters?.[filter.name] || ""}
                onChange={(value: any) => setFilter(filter.name, value)}
                className={`crud-filter-input p-inputtext ${
                  isActive ? "p-filled" : ""
                }`}
                icon={filter.icon}
              />
            ) : (
              React.cloneElement(filter.renderComponent as React.ReactElement, {
                value: activeFilters?.[filter.name],
                onChange: (value: any) => setFilter(filter.name, value),
                className: `crud-filter-input ${
                  isActive ? "p-filled p-inputwrapper-filled" : ""
                }`,
              })
            )}
          </div>
        );
      })}
    </div>
  );
}

export default CrudFilters;
